import React from 'react';
import strings from '../../../strings/strings.json';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from 'theme';
import Table, { TableHeader, TableRow } from 'components/table/Table';
import { EmptyTableContent } from 'components/table/EmptyTableContent';
import { BodyRegular, FlexAlignCenter, FlexEnd, Headline3, WrappingText } from 'components/Elements';
import useWindowWidth from 'hooks/useWindowWidth';
import { useNavigate } from 'react-router-dom';
import ValidateIcon from 'assets/icons/indicators/ValidateIcon';
import MainSection from 'components/sections/MainSection';
import { CourseScheduleOverview } from 'models/CourseScheduleOverview';
import Button from 'components/buttons/Button';
import NextIcon from 'assets/icons/controls/NextIcon';

type LiveCoursesTableProps = {
    liveCourses: CourseScheduleOverview[];
    hideTrainerColumn: boolean;
    activeRowCourseId?: string;
};

export default function LiveCoursesTable({
    liveCourses,
    hideTrainerColumn,
    activeRowCourseId,
}: LiveCoursesTableProps): JSX.Element {
    const { currentWindowWidth } = useWindowWidth();
    const navigate = useNavigate();

    const scrollRef = React.createRef<any>();
    React.useEffect(() => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    });

    const tableHeaders: TableHeader[] = hideTrainerColumn
        ? [
              {
                  name: strings.OAScheduledCoursesTable.tableHeaders.courseTitle,
                  rowPercentage: 35,
              },
              { name: strings.OAScheduledCoursesTable.tableHeaders.startDate, rowPercentage: 15 },
              {
                  name: strings.OAScheduledCoursesTable.tableHeaders.status,
                  rowPercentage: 30,
              },
              {
                  name: '',
                  rowPercentage: 20,
                  mobileRightColumn: true,
              },
          ]
        : [
              {
                  name: strings.OAScheduledCoursesTable.tableHeaders.courseTitle,
                  rowPercentage: 30,
              },
              { name: strings.OAScheduledCoursesTable.tableHeaders.trainer, rowPercentage: 20 },
              { name: strings.OAScheduledCoursesTable.tableHeaders.startDate, rowPercentage: 15 },
              {
                  name: strings.OAScheduledCoursesTable.tableHeaders.status,
                  rowPercentage: 15,
              },
              {
                  name: '',
                  rowPercentage: 20,
                  mobileRightColumn: true,
              },
          ];

    enum Status {
        CANCELLED,
        ENDED,
        NOT_STARTED,
        RUNNING,
    }

    const now = new Date();

    function getStatus(liveCourse: CourseScheduleOverview): Status {
        if (liveCourse.cancelled) {
            return Status.CANCELLED;
        } else if (new Date(liveCourse.endDate) < now) {
            return Status.ENDED;
        } else if (new Date(liveCourse.startDate) < now && new Date(liveCourse.endDate) > now) {
            return Status.RUNNING;
        } else {
            return Status.NOT_STARTED;
        }
    }

    const scheduledCourseRows: TableRow[] = liveCourses
        .sort((a, b) => {
            const aStatus = getStatus(a);
            const bStatus = getStatus(b);
            return bStatus - aStatus;
        })
        .map((liveCourse) => {
            const shortStartDate = new Date(liveCourse.startDate).toLocaleDateString();
            const linkAria = `${strings.OAScheduledCoursesTable.viewCourseButton.aria} ${liveCourse.courseTitle}`;

            let status = getStatus(liveCourse);

            const courseTitleCell = {
                name: strings.OAScheduledCoursesTable.tableHeaders.courseTitle,
                value: (
                    <ScheduledCourseContainer ref={activeRowCourseId === liveCourse.id ? scrollRef : null}>
                        {currentWindowWidth > parseInt(breakpoints.xl) && liveCourse.id === activeRowCourseId && (
                            <ValidateIcon />
                        )}
                        <WrappingText>{liveCourse.courseTitle}</WrappingText>
                    </ScheduledCourseContainer>
                ),
                isBold: true,
            };

            const trainerCell = {
                name: strings.OAScheduledCoursesTable.tableHeaders.trainer,
                isBold: true,
                value:
                    currentWindowWidth < parseInt(breakpoints.md)
                        ? liveCourse.trainer?.email ?? ''
                        : `${liveCourse.trainer?.firstName} ${liveCourse.trainer?.lastName}`,
            };

            const startDateCell = {
                name: strings.OAScheduledCoursesTable.tableHeaders.startDate,
                value: shortStartDate,
            };

            const statusCell = {
                name: strings.OAScheduledCoursesTable.tableHeaders.status,
                value: (
                    <div>
                        <StatusTag
                            $backgroundColour={
                                status === Status.RUNNING ? theme.runningLiveCourse : theme.cardBackground
                            }
                        >
                            <BodyRegular>{status === Status.NOT_STARTED ? 'NOT STARTED' : Status[status]}</BodyRegular>
                        </StatusTag>
                    </div>
                ),
            };

            const buttonCell = {
                name: '',
                value: liveCourse.cancelled ? (
                    ''
                ) : (
                    <FlexEnd>
                        <Button
                            title={strings.OAScheduledCoursesTable.viewCourseButton.title}
                            aria={linkAria}
                            icon={<NextIcon />}
                            onClick={() => {
                                navigate(`/live-course/${liveCourse.id}`);
                            }}
                            iconOnRight
                        />
                    </FlexEnd>
                ),
            };

            const cells = hideTrainerColumn
                ? [courseTitleCell, startDateCell, statusCell, buttonCell]
                : [courseTitleCell, trainerCell, startDateCell, statusCell, buttonCell];

            return {
                id: liveCourse.id,
                cells: cells,
            };
        });

    return (
        <CourseTableContentSection>
            <TitleAndButtonContainer>
                <Headline3>{strings.OAScheduledCoursesTable.liveCoursesHeading}</Headline3>
            </TitleAndButtonContainer>
            <div>
                <Table
                    headers={tableHeaders}
                    rows={scheduledCourseRows}
                    activeRow={activeRowCourseId}
                    mobileViewBelow={breakpoints.md}
                    emptyTableContent={
                        <EmptyTableContent title={strings.OAScheduledCoursesTable.emptyTableContent.title} />
                    }
                />
            </div>
        </CourseTableContentSection>
    );
}

const StatusTag = styled.div<{ $backgroundColour: string }>`
    display: inline-block;
    background-color: ${({ $backgroundColour }) => $backgroundColour};
    border-radius: 32px;
    padding: ${sizes.spacingSm} ${sizes.spacingMd};
`;

const CourseTableContentSection = styled(MainSection)`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
    padding: ${sizes.spacingSm} 0;
    min-height: 120px;

    @media (max-width: ${breakpoints.sm}) {
        padding: unset;
    }
`;

const TitleAndButtonContainer = styled(FlexAlignCenter)`
    justify-content: space-between;
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: ${sizes.spacingMd};
        align-items: flex-start;
    }
`;

const ScheduledCourseContainer = styled.div`
    display: flex;
    align-items: center;
    gap: ${sizes.spacingSm};
`;
